import ResourceManager from "../../components/ResourceManager"
import { useI18n } from "../../hooks"
import { useOptions } from "../../options/hooks/use-options"
import { ProjectViewModel } from "../api/project"
import { StringMap } from "../../types/common"
import { useProjectMutations } from "../hooks/use-project-mutations"

const ProjectManagersWidget = ({ project }: { project: ProjectViewModel }) => {
  const translations = useTranslations(defaultTranslations)
  const { membershipOptions } = useOptions(project.maintainerId)
  const { addManagers, removeManagers } = useProjectMutations()

  const handleAddManagers = (ids: string[]) => {
    const managers = membershipOptions
      .filter((member) => ids.includes(member.id))
      .map((member) => ({ name: member.name, id: member.id }))
    addManagers(project.id, managers)
  }

  const handleRemoveManager = (id: string) => {
    const managers = project.managers
      .filter((member) => member.id === id)
      .map((member) => ({ name: member.name, id: member.id }))
    removeManagers(project.id, managers)
  }

  const sortedMembershipOptions = membershipOptions
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
  const sortedManagers = project.managers
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  return (
    <ResourceManager
      title={translations.title}
      resourceOptions={sortedMembershipOptions}
      resources={sortedManagers}
      onAddResources={handleAddManagers}
      onRemoveResource={handleRemoveManager}
      canAddResource={project.canUpdateManagers}
      canRemoveResource={project.canUpdateManagers}
      emptyOptionsMessage={translations.emptyOptionsMessage}
      emptyResourcesMessage={translations.emptyResourcesMessage}
      isMultipleSelection
    />
  )
}

const useTranslations = (defaults = defaultTranslations): Translations => {
  const { translations: t } = useI18n("project")
  const translations = (t?.managersWidget || {}) as StringMap

  const {
    title = defaults.title,
    emptyOptionsMessage = defaults.emptyOptionsMessage,
    emptyResourcesMessage = defaults.emptyResourcesMessage,
  } = translations

  return {
    title,
    emptyOptionsMessage,
    emptyResourcesMessage,
  }
}

const defaultTranslations = {
  title: "Manager",
  emptyOptionsMessage: "No members in this organisation",
  emptyResourcesMessage: "No manager in this project",
}

type Translations = typeof defaultTranslations

export default ProjectManagersWidget
